'use client';

import { ENTITY_TAB } from '@/constants/entityTabs';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { buildSaleListingHref } from '@/lib/saleCataloguePaths';
import LotContextBar from '@/components/publicSearch/sale-catalogues/LotContextBar';
import {
  SaleLotCatalogueProvider,
  useSaleLotCatalogueContext,
} from '@/hooks/publicSearch/sale-catalogue/SaleLotCatalogueContext';
import { resolveSaleLotDisplayName } from '@/hooks/publicSearch/sale-catalogue/resolveSaleLotDisplayName';
import { Box, Tab, useMediaQuery, useTheme } from '@mui/material';
import { Button, CircularProgress, Typography } from '@/primitives';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GavelIcon from '@mui/icons-material/Gavel';
import PhotoIcon from '@mui/icons-material/Photo';
import NextLink from 'next/link';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import {
  useMemo,
  useState,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  Suspense,
} from 'react';
import { useAnimalView } from '@/hooks/publicSearch/animal/useAnimalView';
import { useAnimalMedia } from '@/hooks/publicSearch/animal/useAnimalMedia';
import { useAnimalSaleHistory } from '@/hooks/publicSearch/animal/useAnimalSaleHistory';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { EventType } from '@/lib/types';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useDatadogTracker } from '@/hooks/publicSearch/datadog/useDatadogTracker';
import { enqueueSnackbar } from '@/providers';
import { useCurrentSociety } from '@/hooks/publicSearch/useCurrentSociety';
import { TABLET_BREAKPOINT } from '@/configs/layout';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import ProgenyTable from '@/components/publicSearch/animal/ProgenyTable';
import PedigreeTab, {
  PEDIGREE_TYPE,
} from '@/components/animals/pedigree/PedigreeTab';
import AnimalMediaTab from '@/components/publicSearch/animal/AnimalMediaTab';
import AnimalEbvTab from '@/components/publicSearch/animal/ebv/AnimalEbvTab';
import AnimalSalesTab from '@/components/publicSearch/animal/AnimalSalesTab';
import AnimalInfoCard from '@/components/publicSearch/animal/AnimalInfoCard';
import { ErrorCowCard } from '@/components/utils/ErrorCowCard';
import { PublicEntityNotFoundCard } from '@/components/publicSearch/PublicEntityNotFoundCard';
import { PublicErrorSection } from '@/components/publicSearch/PublicErrorSection';
import RateLimitBanner from '@/components/publicSearch/RateLimitBanner';
import usePublicSearchLimit from '@/components/publicSearch/heroSection/usePublicSearchLimit';
import { parseAnimalDetailFieldsConfig } from '@/hooks/publicSearch/animal/animalEbvGrid';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { formatEbvPluralLabel } from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { SaleLotDetailView } from '@/components/publicSearch/sale-catalogues/SaleLotDetailView';
import type { SaleLotIdentity } from '@/hooks/publicSearch/sale-catalogue/saleLotDetails';

const PUBLIC_ANIMAL_TAB = {
  EBVS: ENTITY_TAB.EBVS,
  PEDIGREE: ENTITY_TAB.PEDIGREE,
  PROGENY: ENTITY_TAB.PROGENY,
  SALES: ENTITY_TAB.SALES,
  MEDIA: ENTITY_TAB.MEDIA,
} as const;

type PublicAnimalTab =
  (typeof PUBLIC_ANIMAL_TAB)[keyof typeof PUBLIC_ANIMAL_TAB];

const PUBLIC_ANIMAL_TAB_VALUES = Object.values(PUBLIC_ANIMAL_TAB);

export default function Animal({
  lotContext,
}: { lotContext?: SaleLotIdentity } = {}) {
  return lotContext ? (
    <SaleLotDetailView {...lotContext} />
  ) : (
    <RegisteredAnimalDetail />
  );
}

function RegisteredAnimalDetail() {
  const searchParams = useSearchParams();
  const catalogueSaleId = searchParams.get(SEARCH_PARAM.SALE_ID);
  const catalogueLotNo = searchParams.get(SEARCH_PARAM.LOT_NO);
  const pathParams = useParams<{ id: string; orgName: string }>();
  const rawAnimalId = pathParams.id;
  const animalIdFromRoute =
    (Array.isArray(rawAnimalId) ? rawAnimalId[0] : rawAnimalId)?.toString() ??
    '';

  if (catalogueSaleId && catalogueLotNo) {
    return (
      <SaleLotCatalogueProvider
        saleId={catalogueSaleId}
        lotNo={catalogueLotNo}
        animalId={animalIdFromRoute}
      >
        <AnimalDetailContent />
      </SaleLotCatalogueProvider>
    );
  }

  return <AnimalDetailContent />;
}

function AnimalDetailContent() {
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${TABLET_BREAKPOINT}px)`);
  // Exact complement of ProgenyTable's own `down('md')` check, so the two agree
  // on the breakpoint at which it swaps its card list for the data grid.
  const isDesktopBreakpoint = useMediaQuery(breakpoints.up('md'));
  // Phone-only: at 375px the five tabs are 590px of scroller, so labels get
  // sliced mid-word. Stacking the icon over a smaller label fits all five
  // without a scroller (ILRX-4700).
  const isPhone = useMediaQuery(breakpoints.down('sm'));
  const router = useRouter();
  const searchParams = useSearchParams();
  const catalogueSaleId = searchParams.get(SEARCH_PARAM.SALE_ID);
  const catalogueLotNo = searchParams.get(SEARCH_PARAM.LOT_NO);
  const isInSaleCatalogueContext = Boolean(catalogueSaleId && catalogueLotNo);
  const loadErrorTitleId = useId();
  const pathParams = useParams<{ id: string; orgName: string }>();
  const rawAnimalId = pathParams.id;
  const animalIdFromRoute =
    (Array.isArray(rawAnimalId) ? rawAnimalId[0] : rawAnimalId)?.toString() ??
    '';
  const animalIdNumber = Number(animalIdFromRoute);
  const idValid = Number.isInteger(animalIdNumber) && animalIdNumber > 0;

  const currentSociety = useCurrentSociety();
  const orgId = currentSociety.orgId;
  const { hasReachedSearchLimit } = usePublicSearchLimit();
  const animalViewData = useAnimalView(animalIdFromRoute, orgId);
  const {
    activeSales,
    previousSales,
    activeLotNotes,
    loading: salesLoading,
    error: salesError,
  } = useAnimalSaleHistory({ animalId: animalIdFromRoute, orgId });
  const salesListingCount = useMemo(
    () => activeSales.length + previousSales.length,
    [activeSales, previousSales],
  );
  const society = usePublicSearchContext((context) => context.society);
  const isEbvEnabled = useIsEbvEnabled();
  const { ebvLabel } = useEbvDisplayOptions();
  const ebvTabLabel = formatEbvPluralLabel(ebvLabel);
  const saleLotCatalogue = useSaleLotCatalogueContext();
  const lotNavigation = saleLotCatalogue?.navigation;
  const catalogueAnimalDisplayName = useMemo(
    () =>
      resolveSaleLotDisplayName(
        lotNavigation?.currentLot?.lotAnimalName,
        animalViewData.animalDisplayName,
      ),
    [
      lotNavigation?.currentLot?.lotAnimalName,
      animalViewData.animalDisplayName,
    ],
  );
  const { items: animalMediaItems, loading: mediaLoading } = useAnimalMedia({
    animalId: animalIdFromRoute,
    animalDocuments: animalViewData.animalData?.documents ?? [],
    orgId,
  });
  const [tabValue, setTabValue] = useState<PublicAnimalTab>(() => {
    if (typeof window === 'undefined') return PUBLIC_ANIMAL_TAB.EBVS;
    const tab = new URLSearchParams(window.location.search).get(
      SEARCH_PARAM.TAB,
    );
    return tab && PUBLIC_ANIMAL_TAB_VALUES.includes(tab as PublicAnimalTab)
      ? (tab as PublicAnimalTab)
      : PUBLIC_ANIMAL_TAB.EBVS;
  });

  // When the component is reused across animal navigations (no full remount),
  // reset to the default tab rather than inheriting the previous animal's selection.
  // useLayoutEffect fires before paint so the wrong tab is never shown.
  const prevAnimalIdRef = useRef<string>(animalIdFromRoute);

  useLayoutEffect(() => {
    if (prevAnimalIdRef.current === animalIdFromRoute) return;
    prevAnimalIdRef.current = animalIdFromRoute;

    const params = new URLSearchParams(location.search);
    const saleId = params.get(SEARCH_PARAM.SALE_ID);
    const lotNo = params.get(SEARCH_PARAM.LOT_NO);
    const tab = params.get(SEARCH_PARAM.TAB);

    if (saleId && lotNo) {
      if (tab && PUBLIC_ANIMAL_TAB_VALUES.includes(tab as PublicAnimalTab)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- restores the active tab from the URL when navigating to a new animal in sale-catalogue context (ref-guarded reset-on-prop)
        setTabValue(tab as PublicAnimalTab);
      }
      return;
    }

    setTabValue(PUBLIC_ANIMAL_TAB.EBVS);
    history.replaceState({}, '', location.pathname);
  }, [animalIdFromRoute]);

  // The progeny panel fills whatever is left under the tab bar, so it needs
  // that space in real pixels. Measured rather than derived from layout
  // constants because the page scroller's height, this layout's top padding
  // and the tab bar's own height all feed in, and any of them can change
  // without this file noticing.
  const [pageScroller, setPageScroller] = useState<HTMLDivElement | null>(null);
  const [tabBar, setTabBar] = useState<HTMLDivElement | null>(null);
  const [progenyPanelHeight, setProgenyPanelHeight] = useState<number | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!pageScroller || !tabBar) return;

    const measure = () => {
      // `mb` sits outside the bar's border box, so offsetHeight misses it.
      const tabBarBlock =
        tabBar.offsetHeight +
        (parseFloat(window.getComputedStyle(tabBar).marginBottom) || 0);
      // Re-renders only when the measured value actually changes.
      setProgenyPanelHeight((prev) => {
        const next = Math.max(0, pageScroller.clientHeight - tabBarBlock);
        return prev === next ? prev : next;
      });
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(pageScroller);
    observer.observe(tabBar);
    return () => observer.disconnect();
  }, [pageScroller, tabBar]);

  const { trackEvent } = useDatadogTracker();

  // Parse ANIMAL_DETAIL_FIELDS from society settings
  const detailFieldsConfig = useMemo(
    () => parseAnimalDetailFieldsConfig(society.settings),
    [society.settings],
  );

  useEffect(() => {
    if (
      !animalViewData.isLoading &&
      animalViewData.animalIdent &&
      animalViewData.animalName
    ) {
      trackEvent?.(
        'RecordViewed',
        {
          entityType: 'animal',
          entityId: animalViewData.animalIdent,
          entityName: animalViewData.animalName,
          orgName: animalViewData.currentOrg?.name,
          orgId: animalViewData.currentOrg?.organisationId,
          referrer: 'unknown',
        },
        EventType.VIEW_CLICK,
      );
    }
  }, [
    animalViewData.isLoading,
    animalViewData.animalIdent,
    animalViewData.animalName,
    animalViewData.currentOrg?.name,
    animalViewData.currentOrg?.organisationId,
    trackEvent,
  ]);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (!PUBLIC_ANIMAL_TAB_VALUES.includes(newValue as PublicAnimalTab)) return;
    const nextTab = newValue as PublicAnimalTab;
    setTabValue(nextTab);
    const params = new URLSearchParams(location.search);
    params.set(SEARCH_PARAM.TAB, nextTab);
    history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
  };

  useLayoutEffect(() => {
    if (!isEbvEnabled && tabValue === PUBLIC_ANIMAL_TAB.EBVS) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- redirects away from the EBV tab to Pedigree when EBVs are disabled, and syncs the URL via history.replaceState (side-effecting redirect)
      setTabValue(PUBLIC_ANIMAL_TAB.PEDIGREE);
      const params = new URLSearchParams(location.search);
      params.set(SEARCH_PARAM.TAB, PUBLIC_ANIMAL_TAB.PEDIGREE);
      history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
    }
  }, [isEbvEnabled, tabValue]);

  const animalViewSnapshotRef =
    useRef<ReturnType<typeof useAnimalView>>(animalViewData);

  useEffect(() => {
    animalViewSnapshotRef.current = animalViewData;
  });

  useEffect(() => {
    if (!idValid) {
      enqueueSnackbar(
        `Error resolving animal: ${animalIdFromRoute || 'unknown'}. Please try again or contact support.`,
        { variant: SNACKBAR_VARIANT.ERROR },
      );
      return;
    }
    // Avoid false positives: loading/data gaps and brief Apollo error states during refetch.
    if (
      animalViewData.isLoading ||
      animalViewData.animalData ||
      !animalViewData.error
    ) {
      return;
    }
    const id = animalIdFromRoute;
    const timer = window.setTimeout(() => {
      const snapshot = animalViewSnapshotRef.current;
      if (snapshot.isLoading || snapshot.animalData || !snapshot.error) return;
      enqueueSnackbar(
        `Error resolving animal: ${id}. Please try again or contact support.`,
        { variant: SNACKBAR_VARIANT.ERROR },
      );
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    animalIdFromRoute,
    animalViewData.animalData,
    animalViewData.error,
    animalViewData.isLoading,
    idValid,
  ]);

  const handleBack = () => {
    if (isInSaleCatalogueContext && catalogueSaleId) {
      router.push(
        buildSaleListingHref(
          currentSociety.societyCode ?? '',
          catalogueSaleId,
          searchParams,
        ),
      );
      return;
    }
    if (window.history.length > 1) {
      router.back();
      return;
    }
    router.push(`/${currentSociety.societyCode}/${ROUTE_SEGMENT.ANIMALS}`);
  };

  if (!idValid || !orgId) {
    return null;
  }

  // Guest search cap blocks GetAnimalEbvs (the same op this detail view uses)
  // by returning an empty page — which otherwise reads as a false "not found".
  // Show the rate-limit banner instead when the limit is reached and no animal
  // resolved.
  if (
    hasReachedSearchLimit &&
    !animalViewData.isLoading &&
    !animalViewData.animalData
  ) {
    return <RateLimitBanner />;
  }

  if (
    !animalViewData.isLoading &&
    !animalViewData.animalData &&
    animalViewData.error
  ) {
    return (
      <PublicErrorSection ariaLabelledBy={loadErrorTitleId}>
        <ErrorCowCard
          titleId={loadErrorTitleId}
          title="Something went wrong"
          description="We couldn't load this animal page. Please try again or head back to animal search."
          actions={
            <Button
              component={NextLink}
              href={`/${currentSociety.societyCode}/${ROUTE_SEGMENT.ANIMALS}`}
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ textTransform: 'none', px: 2.5, py: 1 }}
            >
              Back to animal search
            </Button>
          }
        />
      </PublicErrorSection>
    );
  }

  if (
    (!animalViewData.isLoading &&
      !animalViewData.animalData &&
      !animalViewData.error) ||
    animalViewData.notFound
  ) {
    return (
      <PublicEntityNotFoundCard
        entityLabel="animal"
        entityId={animalIdFromRoute}
        listHref={`/${currentSociety.societyCode}/${ROUTE_SEGMENT.ANIMALS}`}
        listLabel="Back to animal search"
        onGoBack={handleBack}
      />
    );
  }

  const buildSaleListingPath = (saleId: number, lotNo?: string) => {
    return buildSaleListingHref(
      currentSociety.societyCode ?? '',
      saleId,
      searchParams,
      lotNo ? { lotNo } : undefined,
    );
  };

  // On desktop the Progeny tab sizes itself to the viewport, so it takes the
  // area flush rather than inside the padding the other tabs use — otherwise
  // that padding is added on top of a full-height panel and pushes the grid
  // past the fold. Below `md` ProgenyTable renders a plain card list instead,
  // which still wants the normal tab chrome.
  const isProgenyPanel =
    tabValue === PUBLIC_ANIMAL_TAB.PROGENY && isDesktopBreakpoint;

  return (
    <Box
      ref={setPageScroller}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        '& .MuiTypography-bodylarge': { fontSize: { xs: '14px', md: '16px' } },
        '& .MuiTableCell-root': { fontSize: { xs: '14px', md: '16px' } },
      }}
    >
      {isInSaleCatalogueContext &&
        catalogueSaleId &&
        catalogueLotNo &&
        lotNavigation && (
          <LotContextBar
            orgCode={currentSociety.societyCode ?? ''}
            saleId={catalogueSaleId}
            lotNo={catalogueLotNo}
            navigation={lotNavigation}
          />
        )}
      {/* Animal Information card (title + image + details) */}
      <AnimalInfoCard
        animalViewData={animalViewData}
        orgId={orgId}
        detailFieldsConfig={detailFieldsConfig}
        societyCode={currentSociety.societyCode}
        isInSaleCatalogueContext={isInSaleCatalogueContext}
        animalDisplayNameOverride={
          isInSaleCatalogueContext ? catalogueAnimalDisplayName : undefined
        }
        onBack={handleBack}
      />
      {/* Tabs below */}
      <Box
        sx={{
          pb: isProgenyPanel ? 0 : '16px',
          '& .MuiTabs-indicator': {
            transform: { xs: 'scale(0.9)', md: 'none' },
          },
        }}
      >
        <TabContext value={tabValue}>
          <TabList
            ref={setTabBar}
            aria-label="Animal details tabs"
            allowScrollButtonsMobile={!isPhone}
            scrollButtons={isPhone ? false : 'auto'}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                width: 0,
                overflow: 'hidden',
              },
              ...(isPhone && {
                '& .MuiTab-root': {
                  minWidth: 0,
                  minHeight: 58,
                  px: 0.25,
                  gap: 0.25,
                  fontSize: '10px',
                  letterSpacing: 0,
                  lineHeight: 1.2,
                },
              }),
            }}
            variant={isPhone ? 'fullWidth' : 'scrollable'}
            onChange={handleTabChange}
          >
            {isEbvEnabled && (
              <Tab
                label={ebvTabLabel}
                value={PUBLIC_ANIMAL_TAB.EBVS}
                icon={<BarChartIcon fontSize="small" />}
                iconPosition={isPhone ? 'top' : 'start'}
                sx={{ textTransform: 'none' }}
              />
            )}
            <Tab
              label="Pedigree"
              value={PUBLIC_ANIMAL_TAB.PEDIGREE}
              icon={<AccountTreeIcon fontSize="small" />}
              iconPosition={isPhone ? 'top' : 'start'}
            />
            <Tab
              label="Progeny"
              value={PUBLIC_ANIMAL_TAB.PROGENY}
              icon={<FamilyRestroomIcon fontSize="small" />}
              iconPosition={isPhone ? 'top' : 'start'}
            />
            <Tab
              label={
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Sales
                  <Box
                    component="span"
                    aria-label={`${salesListingCount} sale listings`}
                    sx={{
                      minWidth: 22,
                      height: 22,
                      px: salesListingCount > 9 ? 0.5 : 0,
                      borderRadius: '999px',
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor:
                        salesListingCount > 0
                          ? 'primary.main'
                          : 'action.disabledBackground',
                      color:
                        salesListingCount > 0
                          ? 'primary.contrastText'
                          : 'text.disabled',
                    }}
                  >
                    {salesListingCount > 99 ? '99+' : salesListingCount}
                  </Box>
                </Box>
              }
              value={PUBLIC_ANIMAL_TAB.SALES}
              icon={<GavelIcon fontSize="small" />}
              iconPosition={isPhone ? 'top' : 'start'}
            />
            <Tab
              label="Media"
              value={PUBLIC_ANIMAL_TAB.MEDIA}
              icon={<PhotoIcon fontSize="small" />}
              iconPosition={isPhone ? 'top' : 'start'}
            />
          </TabList>

          <Box
            sx={{
              minHeight: isProgenyPanel ? 0 : '50vh',
              my: isProgenyPanel ? 0 : '16px',
              backgroundColor: 'background.default',
              overflowX: 'auto',
              overflowY: 'visible',
            }}
          >
            <Box sx={{ p: isProgenyPanel ? 0 : '16px' }}>
              {isEbvEnabled && (
                <TabPanel value={PUBLIC_ANIMAL_TAB.EBVS} sx={{ p: 0 }}>
                  <AnimalEbvTab
                    society={society}
                    animalData={animalViewData.animalData}
                    isLoading={animalViewData.isLoading}
                    isMobile={isMobile}
                    societyCode={currentSociety.societyCode}
                    animalIdFromRoute={animalIdFromRoute}
                  />
                </TabPanel>
              )}
              <TabPanel value={PUBLIC_ANIMAL_TAB.PEDIGREE} sx={{ p: 0 }}>
                <PedigreeTab
                  type={PEDIGREE_TYPE.PUBLIC}
                  animalId={animalIdFromRoute}
                  orgId={orgId}
                />
              </TabPanel>
              <TabPanel value={PUBLIC_ANIMAL_TAB.PROGENY} sx={{ p: 0 }}>
                <Suspense
                  fallback={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography
                        variant="bodysmall"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Loading progeny…
                      </Typography>
                    </Box>
                  }
                >
                  <ProgenyTable
                    animalId={animalIdFromRoute}
                    orgId={orgId}
                    panelHeight={progenyPanelHeight ?? undefined}
                  />
                </Suspense>
              </TabPanel>

              <TabPanel value={PUBLIC_ANIMAL_TAB.SALES} sx={{ p: 0 }}>
                <AnimalSalesTab
                  salesLoading={salesLoading}
                  salesError={salesError}
                  activeSales={activeSales}
                  previousSales={previousSales}
                  activeLotNotes={activeLotNotes}
                  buildSaleListingPath={buildSaleListingPath}
                />
              </TabPanel>
              <TabPanel value={PUBLIC_ANIMAL_TAB.MEDIA} sx={{ p: 0 }}>
                <AnimalMediaTab
                  items={animalMediaItems}
                  animalName={
                    isInSaleCatalogueContext
                      ? catalogueAnimalDisplayName
                      : animalViewData.animalDisplayName
                  }
                  loading={animalViewData.isLoading || mediaLoading}
                />
              </TabPanel>
            </Box>
          </Box>
        </TabContext>
      </Box>
    </Box>
  );
}
